/**
 * Header new invoice info
 * @description: This module fetches the new invoice info from the server and updates the UI accordingly.
 *
 * @typedef {Object} InvoiceInfo
 * @property {boolean} showNewInvoiceInfo - Indicates whether to show the new invoice info.
 * @property {string} newInvoicesCount - The count of new invoices.
 */
import {onFind} from "@elements/init-modules-in-scope";

/**
 * Default selectors
 * @type {{base: string}}
 */
const defaultSelectors = {
    base: '.js-new-invoice-info',
    hiddenCls: 'd-none',
    cookieName: 'new_invoice_info',
};

let customerId = null;

/**
 * Check if cookie exists and fetches new invoice info if not.
 * @param {Object} selectors
 */
export function init(selectors = defaultSelectors) {
    onFind(selectors.base, function (baseElement) {
        customerId = baseElement.dataset.newInvoiceInfoCustomerId;
        const invoiceInfoCookie = getCookieValueByName(`${defaultSelectors.cookieName}-${customerId}`);

        if (invoiceInfoCookie) {
            const invoiceCookieInfo = JSON.parse(invoiceInfoCookie)

            if (invoiceCookieInfo.showNewInvoiceInfo) {
                showNewInvoiceInfo(baseElement, invoiceCookieInfo.newInvoicesCount)
            }
        } else {
            getNewInvoiceInfo(baseElement).then(function (data) {
                document.cookie = `${defaultSelectors.cookieName}-${customerId}=${encodeURIComponent(JSON.stringify(data))}; path=/; expires=${new Date(Date.now() + 2 * 60 * 60 * 1000).toUTCString()}`;

                if (!data || !data.showNewInvoiceInfo) return

                showNewInvoiceInfo(baseElement, data.newInvoicesCount)
            })
        }
    });
}

/**
 * Fetches the invoice info content
 * @returns {Promise<InvoiceInfo|null>} The JSON response containing invoice info
 */
const getNewInvoiceInfo = async (baseElement) => {
    try {
        const response = await fetch(getNewInvoiceUrl(baseElement))

        if (!response.ok) {
            console.error(`HTTP error! status: ${response.status}`)

            return null
        }

        return await response.json()
    } catch (error) {
        console.error('Error fetching invoice info:', error)

        return null
    }
};


/**
 * Set invoice count in data-attribute to show it via css and remove hidden class
 * @param {HTMLElement} baseElement
 * @param {string} newInvoicesCount
 */
const showNewInvoiceInfo = (baseElement, newInvoicesCount) => {
    const elements = baseElement.querySelectorAll('[data-new-invoice-info-count]');

    elements.forEach((element) => {
        element.dataset.newInvoiceInfoCount = newInvoicesCount;
        element.classList.remove(defaultSelectors.hiddenCls);
    })
}

/**
 * Get new invoice info URL from data-attribute
 * @param {HTMLElement} baseElement
 * @returns {string}
 */
const getNewInvoiceUrl = (baseElement) => {
    return baseElement.dataset.newInvoiceInfoUrl
}

/**
 * Get cookie value by name
 * @param {string} name
 * @returns {null|string}
 */
const getCookieValueByName = (name) => {
    const cookies = document.cookie.split('; ');
    for (const cookie of cookies) {
        const [key, value] = cookie.split('=');
        if (key === name) {
            return decodeURIComponent(value);
        }
    }
    return null;
}